import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { BASE_URL } from '../context/api';

const Loader = () => (
  <div className="flex items-center justify-center">
    <div className="relative">
      <div className="w-12 h-12 rounded-full absolute border-4 border-pink-200"></div>
      <div className="w-12 h-12 rounded-full animate-spin absolute border-4 border-pink-600 border-t-transparent"></div>
    </div>
  </div>
);

const MakePayment = () => {
    const [formData, setFormData] = useState({
        reg_number: '',
        class_type: '',
        form: '',
        year: '',
        term: '',
        amount_paid: '',
        currency: 'USD',
        payment_reference: ''
    });

    const [classTypes, setClassTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);

    useEffect(() => {
        fetchClassTypes();
    }, []);

    const fetchClassTypes = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/api/class-types`);
            setClassTypes(response.data);
        } catch (error) {
            console.error('Error fetching class types:', error);
            toast.error('Failed to fetch class types.');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const generateReference = () => {
        const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let reference = '';
        for (let i = 0; i < 14; i++) {
            const randomIndex = Math.floor(Math.random() * charset.length);
            reference += charset.charAt(randomIndex);
        }
        setFormData({ ...formData, payment_reference: reference });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await axios.post(`${BASE_URL}/payments/cash`, formData);
            setShowSuccessModal(true);
            setFormData({
                reg_number: '',
                class_type: '',
                form: '',
                year: '',
                term: '',
                amount_paid: '',
                currency: 'USD',
                payment_reference: ''
            });
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to process payment.';
            setErrorMessages([errorMessage]);
            setShowErrorModal(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-5xl mx-auto px-4">
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-gray-800">Make Payment</h1>
                        <div className="h-1 w-full bg-gray-200 mt-2"></div>
                    </div>

                    {loading ? (
                        <Loader />
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
                                    <input
                                        type="text"
                                        name="reg_number"
                                        value={formData.reg_number}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Form</label>
                                    <select
                                        name="form"
                                        value={formData.form}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                        required
                                    >
                                        <option value="">Select Form</option>
                                        {[1, 2, 3, 4, 5, 6].map(form => (
                                            <option key={form} value={form}>Form {form}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Class Type</label>
                                    <select
                                        name="class_type"
                                        value={formData.class_type}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                        required
                                    >
                                        <option value="">Select Class Type</option>
                                        {classTypes.map(type => (
                                            <option key={type.ClassTypeId} value={type.ClassTypeName}>
                                                {type.ClassTypeName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                                    <select
                                        name="year"
                                        value={formData.year}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                        required
                                    >
                                        <option value="">Select Year</option>
                                        {Array.from({ length: 80 }, (_, i) => 2024 + i).map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
                                    <select
                                        name="term"
                                        value={formData.term}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                        required
                                    >
                                        <option value="">Select Term</option>
                                        {[1, 2, 3].map(term => (
                                            <option key={term} value={term}>Term {term}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount Paid</label>
                                    <input
                                        type="number"
                                        name="amount_paid"
                                        value={formData.amount_paid}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                                    <select
                                        name="currency"
                                        value={formData.currency}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                    >
                                        <option value="USD">USD</option>
                                        <option value="ZIG">ZIG</option>
                                    </select>
                                </div>

                                <div className="flex items-end">
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Reference (Optional)</label>
                                        <input
                                            type="text"
                                            name="payment_reference"
                                            value={formData.payment_reference}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={generateReference}
                                        className="ml-2 px-4 py-3 bg-pink-600 text-white rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                                    >
                                        Generate
                                    </button>
                                </div>
                            </div>

                            <div className="flex justify-end">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-6 py-3 bg-pink-600 text-white rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? 'Processing...' : 'Submit Payment'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>

            {/* Error Modal */}
            {showErrorModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg max-w-md w-full">
                        <h3 className="text-lg font-bold text-red-500">Error</h3>
                        <ul className="list-disc list-inside text-red-500 mt-2">
                            {errorMessages.map((msg, index) => (
                                <li key={index}>{msg}</li>
                            ))}
                        </ul>
                        <button
                            onClick={() => setShowErrorModal(false)}
                            className="mt-4 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            {showSuccessModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg max-w-md w-full">
                        <h3 className="text-lg font-bold text-green-700">Payment Successful!</h3>
                        <p className="text-green-700 mt-2">Your payment has been successfully processed.</p>
                        <button
                            onClick={() => setShowSuccessModal(false)}
                            className="mt-4 px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MakePayment;
